import i18next from 'i18next'
import { registerUIKitTranslations } from '@intch/ui-kit/i18n/resources'
import { initReactI18next } from 'react-i18next'

import en from '@/lang/translations/en.json'
import ru from '@/lang/translations/ru.json'

export const LOCALES = ['en', 'ru'] as const
export type Locale = (typeof LOCALES)[number]

const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)

/* The product ships in English, and the surfaces carry no locale control any more. Detection is
   deliberately gone rather than merely unused: with nothing to switch back with, a Russian-locale
   browser would have been handed the Russian build and no way out of it, and a visitor who once
   pressed RU would have stayed there for good.

   The Russian translation stays whole. Every visible string is still a `t()` key present in both
   files — that is what `npm run i18n:check` enforces — so turning a locale back on is a change to
   this constant and a control, not a re-translation. */
const LANGUAGE: Locale = 'en'

/**
 * What a person sees when a key does not resolve. i18next's default answer is THE KEY ITSELF, which is
 * how a primary button came to read `app.steps.confirm` in a screenshot — the call site had been moved
 * to a key the translation files did not carry yet, and for the seconds in between the UI showed our
 * own dotted identifiers to whoever was looking.
 *
 * Dev keeps that default on purpose: a raw key on screen IS the loud signal, next to the warning
 * below, and softening it in dev would hide the miss from the only person able to fix it. Production
 * gets the last segment, humanised — keys here are named after what they say, so `app.steps.confirm`
 * degrades to "Confirm" and the worst case is a slightly odd label instead of leaked internals.
 *
 * `npm run i18n:check` is what stops a missing key from reaching production at all. This is only what
 * happens if one ever gets past it.
 */
const humanise = (key: string) => {
  // The plural suffix belongs to i18next's bookkeeping, not to the sentence: a missing `leads_other`
  // should read "Leads", not "Leads other".
  const last = (key.split('.').pop() ?? key).replace(/_(zero|one|two|few|many|other)$/, '')
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

void i18next.use(initReactI18next).init({
  resources: { en: { translation: en }, ru: { translation: ru } },
  lng: LANGUAGE,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  // A missing key must be loud in dev. `fallbackLng: en` is what hides an untranslated string
  // until it reaches a non-English user in production.
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: import.meta.env.DEV
    ? (lngs, _ns, key) => console.warn(`[i18n] missing key "${key}" for ${lngs.join(',')}`)
    : undefined,
  /* i18next calls this when it fell back to the key AND when a call site's own `defaultValue` was
     used, handing the resolved default as the second argument. Honouring it is not optional: several
     call sites pass a deliberate fallback (a server error string, an empty quote line), and a handler
     that ignored the argument would silently overwrite every one of them. */
  parseMissingKeyHandler: import.meta.env.DEV
    ? undefined
    : (key: string, fallback?: string) => fallback ?? humanise(key),
  /* A key that lands on an OBJECT never reaches the handler above — i18next answers it with the
     sentence "key '…' returned an object instead of string.", which is worse in a button than the key
     was. Same split as above: loud in dev, harmless in production. */
  returnedObjectHandler: import.meta.env.DEV ? undefined : (key: string) => humanise(key),
  returnNull: false,
})

// Переводы компонентов кита (@intch/ui-kit) добавляются в тот же экземпляр после init.
registerUIKitTranslations(i18next)

export const currentLocale = (): Locale => (isLocale(i18next.language) ? i18next.language : 'en')

export { i18next }
